// Client Data Service - Central source of truth for client information
// This connects Operations module with Clients/Staff module

import {
  collection,
  getDocs,
  onSnapshot,
  doc,
  updateDoc,
  addDoc,
  deleteDoc,
  Timestamp
} from 'firebase/firestore';
import { db, storage, auth } from '../config/firebase';
import { onAuthStateChanged } from 'firebase/auth';
import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';

// Family Member interface - represents a linked family member profile
export interface FamilyMember {
  id: string;              // Unique client ID (this is also a full Client profile)
  firstName: string;
  lastName: string;
  phone: string;           // Inherited from parent (same phone)
  relationship: 'son' | 'daughter' | 'spouse' | 'parent' | 'sibling' | 'other';
  age?: number;
  photoURL?: string;
  parentId: string;        // ID of parent who added this member
  isChild: boolean;        // Always true for family members
  notes?: string;
}

export interface Client {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  photo?: string;
  photoURL?: string; // client-set profile photo (customer app → Storage) — shown on the client list + appt card
  preferredBarber?: string;
  membershipType: 'regular' | 'vip' | 'premium' | 'model';
  isModel?: boolean; // MODEL CLIENT (2026-08-01): opted in (via portal) to be a trainee practice model
  joinDate: Date;
  lastVisit?: Date;
  totalVisits: number;
  totalSpent: number;
  averageTicket: number;
  rebookingRate: number;
  noShowCount: number;
  cancellationCount: number;
  notes?: string;
  tags: string[];
  clientTags?: any[];   // structured 3-bucket tags {value,bucket,source,at} (Jose 2026-11-01)
  barberInsight?: string;  // the barber's handling note — the AI follows it (Jose 2026-11-01)
  status: 'active' | 'inactive' | 'dormant' | 'blacklisted';
  loyaltyPoints: number;
  birthday?: string;

  // 👨‍👩‍👧‍👦 FAMILY MANAGEMENT FIELDS
  isParent?: boolean;           // TRUE if this client has family members
  familyMembers?: string[];     // Array of family member IDs
  parentId?: string;            // If this IS a family member, ID of parent
  isChild?: boolean;            // TRUE if this profile is a family member
  relationship?: 'son' | 'daughter' | 'spouse' | 'parent' | 'sibling' | 'other';

  cpiAdjustment?: number; // Manual CPI adjustment from admin

  // 📊 CPI PERSISTENCE FIELDS
  cpiScore?: number;                    // Last calculated CPI score (0-100)
  cpiTier?: 'VIP' | 'Loyal' | 'Regular' | 'New';  // Last calculated tier
  lastCpiUpdate?: string;              // ISO timestamp of last CPI calculation

  // 🔔 REBOOKING REMINDER SETTINGS (opt-in)
  rebookingReminders?: {
    enabled: boolean;           // TRUE if client wants rebooking reminders
    frequency: number;          // How often they come (in weeks): 2, 3, 4, etc.
    reminderTiming: number;     // How many days before to remind: 3, 7, 14, etc.
    lastReminderSent?: Date;    // Track when last reminder was sent
  };

  // 💳 PAYMENT SETTINGS (staff-controlled, per-client)
  paymentSettings?: {
    requirePaymentUpfront: boolean;     // TRUE = must pay online when booking (also the no-show-block master switch)
    allowOnlinePayment: boolean;        // TRUE = online payment option available
    paymentReason?: 'no-shows' | 'policy' | 'special';  // Why payment required ('no-shows' = auto no-show block)
    noShowNotice?: string;              // Custom notice for no-show clients
    noShowBlockedAt?: number;           // epoch ms the no-show block auto-applied (for the 3-service redemption count)
  };

  // ✂️ CUT-TIME (staff-controlled, per-client) — the barber's manual duration knob for this specific
  // client/kid. Either field unset ⇒ auto rules (fade=full, child-booked-with-family=15min). The family
  // override beats BOTH the auto shrink and the fade rule. See shared/serviceDuration.familyMemberDuration.
  cutTimeSettings?: {
    soloMinutes?: number;    // this client's duration when booked ALONE (base-classic knob)
    familyMinutes?: number;  // this client's duration when booked WITH family (base-classic knob)
    // PER-SERVICE, PER-CLIENT minutes (Jose 2026-11-03) — the exact time for a SPECIFIC service for this
    // client ("his medium is 40"). Keyed by normalized service name. Read by shared familyMemberDuration.
    serviceTimes?: Record<string, { family?: number; solo?: number }>;
  };

  // 🚨 EMERGENCY BOOKING PAYMENT SETTINGS
  emergencyBookingSettings?: {
    allowOnlinePayment: boolean;        // Enable online payment for emergency bookings
    waiveFee: boolean;                  // Waive the emergency booking fee (default: true)
    feeAmount: number;                  // Emergency booking service fee amount (e.g., 25)
    blockSqueezing?: boolean;           // Prevent emergency appointments from being squeezed in around this client's bookings
  };

  // 🎯 SERVICE-LEVEL EMERGENCY SQUEEZE OVERRIDES
  // For trained clients whose restricted services (fades/shaves) take less time
  emergencyServiceOverrides?: Array<{
    serviceVariationId: string;         // The service variation ID
    serviceName: string;                // Display name for reference
    allowEmergencySqueeze: boolean;     // Override: allow squeeze-in even if service is normally restricted
    grantedBy: string;                  // Admin/staff who granted this override
    grantedAt: Date;                    // When the override was granted
    reason?: string;                    // Why this client gets the override (e.g., "Trained client - fade takes 15min")
  }>;

  // 🔒 GRANDFATHERED PRICING (Price Lock with Approval)
  grandfatheredPricing?: {
    isActive: boolean;                  // TRUE if grandfathered pricing is approved and active
    amount: number;                     // Locked-in price for this client
    serviceType: string;                // Type of service this price applies to (e.g., "Haircut", "All Services")
    reason: string;                     // Why this price was granted
    requestedBy: string;                // Staff member who requested
    approvedBy?: string;                // Manager/admin who approved
    requestDate: Date;                  // When request was submitted
    approvalDate?: Date;                // When request was approved
    status: 'pending' | 'approved' | 'denied';  // Current status
    denialReason?: string;              // If denied, why
  };

  preferences?: {
    appointmentReminders: boolean;
    marketingEmails: boolean;
    smsNotifications: boolean;
    preferredDays?: string[];
    preferredTimes?: string[];
    allergies?: string[];
    specialRequests?: string[];
  };

  // 💳 STRIPE SAVED PAYMENT METHODS
  stripeCustomerId?: string;  // Stripe Customer ID for this client
  savedPaymentMethods?: SavedPaymentMethod[];
}

// Saved Payment Method interface
export interface SavedPaymentMethod {
  id: string;
  stripePaymentMethodId: string;
  brand: 'visa' | 'mastercard' | 'amex' | 'discover' | 'unknown';
  last4: string;
  expiryMonth: number;
  expiryYear: number;
  isDefault: boolean;
  createdAt: Date;
}

// Sample client data extracted from reports
// These are real clients from your barbershop data
const clientsDatabase: Client[] = [
  // VIP Clients
  {
    id: 'c001',
    firstName: 'Michael',
    lastName: 'Johnson',
    email: 'michael.j@email.com',
    phone: '(555) 123-4567',
    preferredBarber: 'Jose',
    membershipType: 'vip',
    joinDate: new Date('2022-03-15'),
    lastVisit: new Date('2024-09-18'),
    totalVisits: 52,
    totalSpent: 2340,
    averageTicket: 45,
    rebookingRate: 95,
    noShowCount: 0,
    cancellationCount: 2,
    tags: ['loyal', 'high-spender', 'referrer'],
    status: 'active',
    loyaltyPoints: 2340,
    birthday: '1985-07-12',
    preferences: {
      appointmentReminders: true,
      marketingEmails: true,
      smsNotifications: true,
      preferredDays: ['Thursday', 'Friday'],
      preferredTimes: ['evening'],
      specialRequests: ['Beard trim with every cut']
    }
  },
  {
    id: 'c002',
    firstName: 'David',
    lastName: 'Martinez',
    email: 'david.martinez@email.com',
    phone: '(555) 234-5678',
    preferredBarber: 'James',
    membershipType: 'vip',
    joinDate: new Date('2021-11-20'),
    lastVisit: new Date('2024-09-19'),
    totalVisits: 78,
    totalSpent: 3510,
    averageTicket: 45,
    rebookingRate: 98,
    noShowCount: 1,
    cancellationCount: 3,
    tags: ['loyal', 'weekly-client', 'high-tipper'],
    status: 'active',
    loyaltyPoints: 3510,
    birthday: '1978-03-22'
  },

  // Premium Clients
  {
    id: 'c003',
    firstName: 'Robert',
    lastName: 'Williams',
    email: 'rob.williams@email.com',
    phone: '(555) 345-6789',
    preferredBarber: 'Jose',
    membershipType: 'premium',
    joinDate: new Date('2023-01-10'),
    lastVisit: new Date('2024-09-15'),
    totalVisits: 35,
    totalSpent: 1400,
    averageTicket: 40,
    rebookingRate: 88,
    noShowCount: 2,
    cancellationCount: 4,
    tags: ['professional', 'corporate'],
    status: 'active',
    loyaltyPoints: 1400
  },
  {
    id: 'c004',
    firstName: 'James',
    lastName: 'Anderson',
    email: 'j.anderson@email.com',
    phone: '(555) 456-7890',
    preferredBarber: 'Kelly',
    membershipType: 'premium',
    joinDate: new Date('2022-08-05'),
    lastVisit: new Date('2024-09-17'),
    totalVisits: 42,
    totalSpent: 1890,
    averageTicket: 45,
    rebookingRate: 90,
    noShowCount: 0,
    cancellationCount: 1,
    tags: ['beard-specialist', 'product-buyer'],
    status: 'active',
    loyaltyPoints: 1890
  },

  // Model Clients (High performers)
  {
    id: 'c005',
    firstName: 'Christopher',
    lastName: 'Lee',
    email: 'chris.lee@email.com',
    phone: '(555) 567-8901',
    preferredBarber: 'Jose',
    membershipType: 'model',
    joinDate: new Date('2021-06-15'),
    lastVisit: new Date('2024-09-20'),
    totalVisits: 104,
    totalSpent: 5200,
    averageTicket: 50,
    rebookingRate: 100,
    noShowCount: 0,
    cancellationCount: 0,
    tags: ['perfect-attendance', 'influencer', 'brand-ambassador'],
    status: 'active',
    loyaltyPoints: 5200,
    notes: 'Instagram influencer - tags us in posts'
  },

  // Regular Clients
  {
    id: 'c006',
    firstName: 'Daniel',
    lastName: 'Brown',
    email: 'dan.brown@email.com',
    phone: '(555) 678-9012',
    preferredBarber: 'James',
    membershipType: 'regular',
    joinDate: new Date('2024-01-15'),
    lastVisit: new Date('2024-09-10'),
    totalVisits: 12,
    totalSpent: 420,
    averageTicket: 35,
    rebookingRate: 75,
    noShowCount: 1,
    cancellationCount: 2,
    tags: ['new-client', 'student'],
    status: 'active',
    loyaltyPoints: 420
  },
  {
    id: 'c007',
    firstName: 'Matthew',
    lastName: 'Garcia',
    email: 'matt.garcia@email.com',
    phone: '(555) 789-0123',
    preferredBarber: 'Marissa Frasso',
    membershipType: 'regular',
    joinDate: new Date('2023-09-20'),
    lastVisit: new Date('2024-09-14'),
    totalVisits: 18,
    totalSpent: 630,
    averageTicket: 35,
    rebookingRate: 80,
    noShowCount: 0,
    cancellationCount: 1,
    tags: ['monthly-client'],
    status: 'active',
    loyaltyPoints: 630
  },
  {
    id: 'c008',
    firstName: 'Anthony',
    lastName: 'Rodriguez',
    email: 'anthony.r@email.com',
    phone: '(555) 890-1234',
    preferredBarber: 'Kelly',
    membershipType: 'regular',
    joinDate: new Date('2023-05-10'),
    lastVisit: new Date('2024-08-30'),
    totalVisits: 20,
    totalSpent: 700,
    averageTicket: 35,
    rebookingRate: 70,
    noShowCount: 3,
    cancellationCount: 5,
    tags: ['at-risk'],
    status: 'active',
    loyaltyPoints: 700,
    notes: 'Tends to cancel last minute'
  },

  // Dormant Clients (need reactivation)
  {
    id: 'c009',
    firstName: 'Joseph',
    lastName: 'Thompson',
    email: 'joe.thompson@email.com',
    phone: '(555) 901-2345',
    preferredBarber: 'Jose',
    membershipType: 'regular',
    joinDate: new Date('2022-12-01'),
    lastVisit: new Date('2024-06-15'),
    totalVisits: 15,
    totalSpent: 525,
    averageTicket: 35,
    rebookingRate: 60,
    noShowCount: 2,
    cancellationCount: 3,
    tags: ['dormant', 'win-back'],
    status: 'dormant',
    loyaltyPoints: 525,
    notes: 'Hasn\'t visited in 3+ months'
  },
  {
    id: 'c010',
    firstName: 'Kevin',
    lastName: 'White',
    email: 'kevin.w@email.com',
    phone: '(555) 012-3456',
    preferredBarber: 'James',
    membershipType: 'regular',
    joinDate: new Date('2023-03-15'),
    lastVisit: new Date('2024-07-01'),
    totalVisits: 10,
    totalSpent: 350,
    averageTicket: 35,
    rebookingRate: 50,
    noShowCount: 4,
    cancellationCount: 6,
    tags: ['dormant', 'high-risk'],
    status: 'dormant',
    loyaltyPoints: 350
  },

  // Walk-in converts
  {
    id: 'c011',
    firstName: 'Brian',
    lastName: 'Hall',
    email: 'brian.hall@email.com',
    phone: '(555) 123-9876',
    membershipType: 'regular',
    joinDate: new Date('2024-09-01'),
    lastVisit: new Date('2024-09-18'),
    totalVisits: 3,
    totalSpent: 105,
    averageTicket: 35,
    rebookingRate: 100,
    noShowCount: 0,
    cancellationCount: 0,
    tags: ['walk-in-convert', 'new'],
    status: 'active',
    loyaltyPoints: 105
  },
  {
    id: 'c012',
    firstName: 'Eric',
    lastName: 'Young',
    email: 'eric.young@email.com',
    phone: '(555) 234-8765',
    membershipType: 'regular',
    joinDate: new Date('2024-08-15'),
    lastVisit: new Date('2024-09-16'),
    totalVisits: 4,
    totalSpent: 140,
    averageTicket: 35,
    rebookingRate: 75,
    noShowCount: 0,
    cancellationCount: 0,
    tags: ['walk-in-convert', 'potential-vip'],
    status: 'active',
    loyaltyPoints: 140,
    preferredBarber: 'Marissa Frasso'
  }
];

class ClientDataService {
  private clients: Client[] = [];
  private clientsCollection = collection(db, 'clients');
  private listeners: ((clients: Client[]) => void)[] = [];
  private isInitialized = false;
  private _clientsUnsub: (() => void) | null = null; // clients listener handle (for auth re-subscribe)

  constructor() {
    this.initializeRealTimeSync();
    // Load from localStorage as fallback
    this.loadFromLocalStorage();
    // RESILIENCE (Jose 2026-10-29 audit): clients listener can attach pre-auth; it has an error cb +
    // localStorage fallback, but re-subscribe on auth-ready so it goes live instead of staying on cache.
    try { onAuthStateChanged(auth, (u) => { if (u) this.initializeRealTimeSync(); }); } catch { /* ignore */ }
  }

  // Initialize real-time sync with Firebase
  private initializeRealTimeSync() {
    try { this._clientsUnsub?.(); } catch { /* ignore */ }
    this._clientsUnsub = onSnapshot(this.clientsCollection, (snapshot) => {
      const firebaseClients: Client[] = [];
      snapshot.forEach((doc) => {
        const data = doc.data();
        // ABSORBED RECORD GUARD (Jose 2026-10-23): a doc with `mergedInto` was folded into another client
        // (by signup auto-link OR the dedupe merge) — its appointments/history now live on the surviving
        // account. Skip it so it NEVER shows as a duplicate row anywhere (Clients list, search, pickers,
        // stats). THIS was the missing piece: the merge ran, but the ghost row was never hidden.
        if ((data as any).mergedInto) return;
        firebaseClients.push({
          // DROP-ON-READ FIX (2026-08-07): spread the full doc first so fields this mapper doesn't
          // list — paymentSettings (the no-show block!), emergencyBookingSettings, etc. — survive the
          // read. The typed conversions below override the raw Timestamps. (Same trap as barbers.)
          ...(data as any),
          id: doc.id,
          firstName: data.firstName || '',
          lastName: data.lastName || '',
          email: data.email || '',
          phone: data.phone || '',
          preferredBarber: data.preferredBarber,
          membershipType: data.membershipType || 'regular',
          joinDate: data.joinDate?.toDate() || new Date(),
          lastVisit: data.lastVisit?.toDate(),
          totalVisits: data.totalVisits || 0,
          totalSpent: data.totalSpent || 0,
          averageTicket: data.averageTicket || 0,
          rebookingRate: data.rebookingRate || 0,
          noShowCount: data.noShowCount || 0,
          cancellationCount: data.cancellationCount || 0,
          notes: data.notes,
          tags: data.tags || [],
          status: data.status || 'active',
          loyaltyPoints: data.loyaltyPoints || 0,
          birthday: data.birthday,
          familyMembers: data.familyMembers,
          cpiAdjustment: data.cpiAdjustment,
          cpiScore: data.cpiScore,
          cpiTier: data.cpiTier,
          lastCpiUpdate: data.lastCpiUpdate,
          preferences: data.preferences,
          // Payment fields
          stripeCustomerId: data.stripeCustomerId,
          savedPaymentMethods: data.savedPaymentMethods?.map((pm: any) => ({
            ...pm,
            createdAt: pm.createdAt?.toDate ? pm.createdAt.toDate() : new Date(pm.createdAt)
          })) || []
        });
      });

      // If Firebase has data, use it. Otherwise, use fallback data
      if (firebaseClients.length > 0) {
        this.clients = firebaseClients;
        this.isInitialized = true;
      } else if (!this.isInitialized) {
        // No Firebase data and not initialized - use sample data but push to Firebase
        this.clients = clientsDatabase;
        this.syncSampleDataToFirebase();
      }

      this.saveToLocalStorage();
      this.notifyListeners();
    }, (error) => {
      console.error('Firebase sync error:', error);
      // Fall back to localStorage data
      this.loadFromLocalStorage();
      if (this.clients.length === 0) {
        // If no localStorage data either, use sample data
        this.clients = clientsDatabase;
        this.saveToLocalStorage();
      }
    });
  }

  // Sync sample data to Firebase if collection is empty
  private async syncSampleDataToFirebase() {
    try {
      for (const client of clientsDatabase) {
        const { id, ...clientData } = client;
        await addDoc(this.clientsCollection, {
          ...clientData,
          joinDate: Timestamp.fromDate(client.joinDate),
          lastVisit: client.lastVisit ? Timestamp.fromDate(client.lastVisit) : null
        });
      }
      console.log('Sample data synced to Firebase');
    } catch (error) {
      console.error('Error syncing sample data:', error);
    }
  }

  // Subscribe to changes
  subscribe(callback: (clients: Client[]) => void): () => void {
    this.listeners.push(callback);
    // Immediately call with current data
    callback(this.clients);

    // Return unsubscribe function
    return () => {
      const index = this.listeners.indexOf(callback);
      if (index > -1) {
        this.listeners.splice(index, 1);
      }
    };
  }

  // Notify all listeners
  private notifyListeners() {
    this.listeners.forEach(callback => callback(this.clients));
  }

  // Get all clients - synchronous version for backward compatibility
  getAllClients(): Client[] {
    return this.clients;
  }

  // Get all clients - async version
  async getAllClientsAsync(): Promise<Client[]> {
    if (this.clients.length === 0) {
      // Try to fetch from Firebase
      try {
        const snapshot = await getDocs(this.clientsCollection);
        const clients: Client[] = [];
        snapshot.forEach((doc) => {
          const data = doc.data();
          clients.push({
            ...this.convertFirebaseClient(data),
            id: doc.id
          });
        });
        if (clients.length > 0) {
          this.clients = clients;
          this.saveToLocalStorage();
        }
      } catch (error) {
        console.error('Error fetching clients:', error);
      }
    }
    return this.clients;
  }

  // Convert Firebase data to Client type
  private convertFirebaseClient(data: any): Omit<Client, 'id'> {
    return {
      // DROP-ON-READ FIX (2026-08-07): spread first so paymentSettings (no-show block),
      // emergencyBookingSettings, and any other unlisted fields survive; typed conversions below win.
      ...(data as any),
      firstName: data.firstName || '',
      lastName: data.lastName || '',
      email: data.email || '',
      phone: data.phone || '',
      preferredBarber: data.preferredBarber,
      membershipType: data.membershipType || 'regular',
      joinDate: data.joinDate?.toDate() || new Date(),
      lastVisit: data.lastVisit?.toDate(),
      totalVisits: data.totalVisits || 0,
      totalSpent: data.totalSpent || 0,
      averageTicket: data.averageTicket || 0,
      rebookingRate: data.rebookingRate || 0,
      noShowCount: data.noShowCount || 0,
      cancellationCount: data.cancellationCount || 0,
      notes: data.notes,
      tags: data.tags || [],
      status: data.status || 'active',
      loyaltyPoints: data.loyaltyPoints || 0,
      birthday: data.birthday,
      familyMembers: data.familyMembers,
      cpiAdjustment: data.cpiAdjustment,
      cpiScore: data.cpiScore,
      cpiTier: data.cpiTier,
      lastCpiUpdate: data.lastCpiUpdate,
      preferences: data.preferences,
      // Payment fields
      stripeCustomerId: data.stripeCustomerId,
      savedPaymentMethods: data.savedPaymentMethods?.map((pm: any) => ({
        ...pm,
        createdAt: pm.createdAt?.toDate ? pm.createdAt.toDate() : new Date(pm.createdAt)
      })) || []
    };
  }

  // Get client by ID - synchronous
  getClientById(id: string): Client | undefined {
    return this.clients.find(client => client.id === id);
  }

  // Get clients by barber
  getClientsByBarber(barberName: string): Client[] {
    return this.clients.filter(client => client.preferredBarber === barberName);
  }

  // Get clients by membership type
  getClientsByMembership(type: 'regular' | 'vip' | 'premium' | 'model'): Client[] {
    return this.clients.filter(client => client.membershipType === type);
  }

  // Get active clients
  getActiveClients(): Client[] {
    return this.clients.filter(client => client.status === 'active');
  }

  // Get dormant clients (for win-back campaigns)
  getDormantClients(): Client[] {
    const thirtyDaysAgo = new Date();
    thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);

    return this.clients.filter(client => {
      if (!client.lastVisit) return true;
      return client.lastVisit < thirtyDaysAgo;
    });
  }

  // Get clients at risk (low rebooking rate or high cancellations)
  getAtRiskClients(): Client[] {
    return this.clients.filter(client =>
      client.rebookingRate < 70 ||
      client.noShowCount > 2 ||
      client.cancellationCount > 3
    );
  }

  // Get VIP and Premium clients
  getHighValueClients(): Client[] {
    return this.clients.filter(client =>
      client.membershipType === 'vip' ||
      client.membershipType === 'premium' ||
      client.membershipType === 'model'
    );
  }

  // Search clients
  searchClients(query: string): Client[] {
    // Jose 2026-10-30: the walk-in picker must find a client when the operator types the FULL name
    // ("Matt Wilkinson") OR a phone in any format. The old code tested the typed string against first
    // and last name SEPARATELY, so a full name matched neither; and phone was a raw substring test. Now
    // match the combined "first last" name + normalize the phone to digits so any format hits. Also
    // null-guards every field (undefined email/phone used to be able to throw).
    const q = (query || '').toLowerCase().trim();
    if (!q) return [];
    const qDigits = q.replace(/\D/g, '');
    return this.clients.filter(client => {
      const first = (client.firstName || '').toLowerCase();
      const last = (client.lastName || '').toLowerCase();
      const full = `${first} ${last}`.trim();
      const email = (client.email || '').toLowerCase();
      const phoneDigits = (client.phone || '').replace(/\D/g, '');
      return first.includes(q) || last.includes(q) || full.includes(q) ||
        email.includes(q) ||
        (qDigits.length >= 3 && phoneDigits.includes(qDigits));
    });
  }

  // Add new client
  // CSV-IMPORT TIGHTENING (2026-08-01): normalize phone for dedup.
  private normPhone(p?: string): string { return (p || '').replace(/\D/g, ''); }

  // Resolve + cache this shop's id, so imported/created clients are tenant-tagged.
  private cachedShopId: string | null = null;
  private async resolveShopId(): Promise<string | null> {
    if (this.cachedShopId) return this.cachedShopId;
    try {
      const snap = await getDocs(collection(db, 'shops'));
      if (!snap.empty) { this.cachedShopId = snap.docs[0].id; return this.cachedShopId; }
    } catch { /* offline / no shop yet */ }
    return null;
  }

  // Add a client — DEDUP by phone (upsert) + tag shopId. Prevents the duplicate-import
  // problem (real Fresha data can be re-imported safely; existing clients are updated, not cloned).
  async addClient(client: Omit<Client, 'id'>): Promise<Client> {
    const phone = this.normPhone(client.phone);
    if (phone) {
      const existing = this.clients.find(c => this.normPhone(c.phone) === phone);
      if (existing) {
        const merged = await this.updateClient(existing.id, { ...client } as Partial<Client>);
        return merged || existing;
      }
    }
    const shopId = await this.resolveShopId();
    try {
      const docRef = await addDoc(this.clientsCollection, {
        ...client,
        ...(shopId ? { shopId } : {}),
        joinDate: Timestamp.fromDate(client.joinDate),
        lastVisit: client.lastVisit ? Timestamp.fromDate(client.lastVisit) : null
      });
      const newClient: Client = {
        ...client,
        id: docRef.id
      };
      this.clients.push(newClient);
      this.saveToLocalStorage();
      this.notifyListeners();
      return newClient;
    } catch (error) {
      console.error('Error adding client:', error);
      // Fallback to local only
      const newClient: Client = {
        ...client,
        id: `c${Date.now()}`
      };
      this.clients.push(newClient);
      this.saveToLocalStorage();
      return newClient;
    }
  }

  // Update client
  async updateClient(id: string, updates: Partial<Client>): Promise<Client | undefined> {
    try {
      const docRef = doc(this.clientsCollection, id);
      await updateDoc(docRef, updates as any);

      const index = this.clients.findIndex(c => c.id === id);
      if (index !== -1) {
        this.clients[index] = { ...this.clients[index], ...updates };
        this.saveToLocalStorage();
        this.notifyListeners();
        return this.clients[index];
      }
    } catch (error) {
      console.error('Error updating client:', error);
      // Fallback to local only
      const index = this.clients.findIndex(c => c.id === id);
      if (index !== -1) {
        this.clients[index] = { ...this.clients[index], ...updates };
        this.saveToLocalStorage();
        return this.clients[index];
      }
    }
    return undefined;
  }

  // Delete client
  async deleteClient(id: string): Promise<void> {
    try {
      // Delete from Firestore
      const docRef = doc(this.clientsCollection, id);
      await deleteDoc(docRef);

      // Delete photo from storage if exists
      const client = this.clients.find(c => c.id === id);
      if (client?.photo && client.photo.startsWith('http')) {
        try {
          const photoRef = ref(storage, `clients/${id}/profile`);
          await deleteObject(photoRef);
        } catch (error) {
          console.log('No photo to delete or error deleting photo:', error);
        }
      }

      // Remove from local array
      this.clients = this.clients.filter(c => c.id !== id);
      this.saveToLocalStorage();
      this.notifyListeners();
    } catch (error) {
      console.error('Error deleting client:', error);
      // Fallback to local only
      this.clients = this.clients.filter(c => c.id !== id);
      this.saveToLocalStorage();
      this.notifyListeners();
    }
  }

  // Check in client
  checkInClient(clientId: string, barberId?: string): void {
    const client = this.getClientById(clientId);
    if (client) {
      this.updateClient(clientId, {
        lastVisit: new Date(),
        totalVisits: client.totalVisits + 1
      });
    }
  }

  // Save to localStorage
  private saveToLocalStorage(): void {
    localStorage.setItem('slikpos_clients', JSON.stringify(this.clients));
  }

  // Load from localStorage
  loadFromLocalStorage(): void {
    const stored = localStorage.getItem('slikpos_clients');
    if (stored) {
      try {
        const parsed = JSON.parse(stored);
        // Convert date strings back to Date objects
        this.clients = parsed.map((c: any) => ({
          ...c,
          joinDate: new Date(c.joinDate),
          lastVisit: c.lastVisit ? new Date(c.lastVisit) : undefined
        }));
      } catch (error) {
        console.error('Error loading from localStorage:', error);
      }
    }
  }

  // Get client statistics
  getClientStats() {
    const clients = this.getAllClients();
    const total = clients.length;
    if (total === 0) {
      return {
        total: 0,
        active: 0,
        vip: 0,
        premium: 0,
        model: 0,
        regular: 0,
        dormant: 0,
        atRisk: 0,
        avgRetentionRate: 0,
        totalRevenue: 0,
        avgTicket: 0
      };
    }

    const active = clients.filter(c => c.status === 'active').length;
    const vip = clients.filter(c => c.membershipType === 'vip').length;
    const premium = clients.filter(c => c.membershipType === 'premium').length;
    const model = clients.filter(c => c.membershipType === 'model').length;
    const dormant = this.getDormantClients().length;
    const atRisk = this.getAtRiskClients().length;

    return {
      total,
      active,
      vip,
      premium,
      model,
      regular: total - vip - premium - model,
      dormant,
      atRisk,
      avgRetentionRate: Math.round(
        clients.reduce((sum, c) => sum + c.rebookingRate, 0) / total
      ),
      totalRevenue: clients.reduce((sum, c) => sum + c.totalSpent, 0),
      avgTicket: Math.round(
        clients.reduce((sum, c) => sum + c.averageTicket, 0) / total
      )
    };
  }

  // Upload photo to Firebase Storage
  async uploadPhoto(clientId: string, file: File): Promise<string> {
    try {
      const storageRef = ref(storage, `clients/${clientId}/${file.name}`);
      await uploadBytes(storageRef, file);
      const downloadURL = await getDownloadURL(storageRef);
      return downloadURL;
    } catch (error) {
      console.error('Error uploading photo:', error);
      throw error;
    }
  }

  // Delete photo from Firebase Storage
  async deletePhoto(photoURL: string): Promise<void> {
    try {
      const photoRef = ref(storage, photoURL);
      await deleteObject(photoRef);
    } catch (error) {
      console.error('Error deleting photo:', error);
      // Don't throw - photo might already be deleted
    }
  }

  // 👨‍👩‍👧‍👦 FAMILY MEMBER METHODS

  /**
   * Add a family member to a parent's profile
   * Creates a new Client profile for the family member and links them
   */
  async addFamilyMember(
    parentId: string,
    familyMemberData: {
      firstName: string;
      lastName: string;
      relationship: 'son' | 'daughter' | 'spouse' | 'parent' | 'sibling' | 'other';
      age?: number;
      notes?: string;
    }
  ): Promise<Client> {
    try {
      // Get parent client
      const parent = this.getClientById(parentId);
      if (!parent) {
        throw new Error('Parent client not found');
      }

      // Create new client profile for family member
      const familyMemberClient: Omit<Client, 'id'> = {
        firstName: familyMemberData.firstName,
        lastName: familyMemberData.lastName || parent.lastName, // Inherit last name if not provided
        email: '', // Family members don't need separate email
        phone: parent.phone, // ⭐ SAME PHONE as parent
        membershipType: 'regular',
        joinDate: new Date(),
        totalVisits: 0,
        totalSpent: 0,
        averageTicket: 0,
        rebookingRate: 0,
        noShowCount: 0,
        cancellationCount: 0,
        tags: ['family-member'],
        status: 'active',
        loyaltyPoints: 0,
        notes: familyMemberData.notes,

        // 👨‍👩‍👧‍👦 Family fields
        isChild: true,
        parentId: parentId,
        relationship: familyMemberData.relationship,

        preferences: {
          appointmentReminders: parent.preferences?.appointmentReminders || false,
          marketingEmails: false,
          smsNotifications: parent.preferences?.smsNotifications || false
        }
      };

      // Add to database
      const newFamilyMember = await this.addClient(familyMemberClient);

      // Update parent to mark as having family members
      const currentFamilyMembers = parent.familyMembers || [];
      await this.updateClient(parentId, {
        isParent: true,
        familyMembers: [...currentFamilyMembers, newFamilyMember.id]
      });

      return newFamilyMember;
    } catch (error) {
      console.error('Error adding family member:', error);
      throw error;
    }
  }

  /**
   * Get all family members for a parent
   */
  getFamilyMembers(parentId: string): Client[] {
    const parent = this.getClientById(parentId);
    if (!parent || !parent.familyMembers || parent.familyMembers.length === 0) {
      return [];
    }

    return parent.familyMembers
      .map(memberId => this.getClientById(memberId))
      .filter((member): member is Client => member !== undefined);
  }

  /**
   * Get parent of a family member
   */
  getParent(familyMemberId: string): Client | undefined {
    const familyMember = this.getClientById(familyMemberId);
    if (!familyMember || !familyMember.parentId) {
      return undefined;
    }

    return this.getClientById(familyMember.parentId);
  }

  /**
   * Get all clients by phone number (parent + all family members)
   * This is used for "Who is this for?" selection
   */
  getClientsByPhone(phone: string): Client[] {
    // PHONE-KEY FIX (2026-08-07): compare DIGITS-ONLY on both sides. Callers pass normalized
    // digits (e.g. "8569799044") but stored phones are formatted ("(856) 979-9044"), so the old
    // strict `===` never matched — that silently broke the no-show block's by-phone lookup.
    const target = (phone || '').replace(/\D/g, '');
    if (!target) return [];
    return this.clients.filter(client => (client.phone || '').replace(/\D/g, '') === target);
  }

  /**
   * Remove a family member from a parent's profile
   */
  async removeFamilyMember(parentId: string, familyMemberId: string): Promise<void> {
    try {
      const parent = this.getClientById(parentId);
      if (!parent) {
        throw new Error('Parent client not found');
      }

      // Remove from parent's family members list
      const updatedFamilyMembers = (parent.familyMembers || []).filter(id => id !== familyMemberId);

      await this.updateClient(parentId, {
        familyMembers: updatedFamilyMembers,
        isParent: updatedFamilyMembers.length > 0 // Set false if no more family members
      });

      // Optionally delete the family member's client profile
      // Or just mark them as inactive
      await this.updateClient(familyMemberId, {
        status: 'inactive',
        parentId: undefined,
        isChild: false
      });

    } catch (error) {
      console.error('Error removing family member:', error);
      throw error;
    }
  }

  /**
   * Check if a client is a parent (has family members)
   */
  isParentClient(clientId: string): boolean {
    const client = this.getClientById(clientId);
    return client?.isParent === true && (client.familyMembers?.length || 0) > 0;
  }

  /**
   * Check if a client is a family member (child profile)
   */
  isFamilyMember(clientId: string): boolean {
    const client = this.getClientById(clientId);
    return client?.isChild === true && !!client.parentId;
  }

  /**
   * 🔄 AUTO-UPDATE CLIENT STATS BASED ON APPOINTMENTS
   * This should be called after appointments are completed
   */
  async updateClientStatsFromAppointments(clientId: string): Promise<void> {
    try {
      const client = this.getClientById(clientId);
      if (!client) {
        console.warn(`Client ${clientId} not found for stats update`);
        return;
      }

      // Import appointment service dynamically to avoid circular dependency
      const { appointmentService } = await import('./appointmentService');

      // Get all completed appointments for this client
      const clientAppointments = appointmentService.getAppointmentsByClient(clientId);
      const completedAppointments = clientAppointments.filter(apt => apt.status === 'completed');

      // Calculate stats
      const totalVisits = completedAppointments.length;
      const totalSpent = completedAppointments.reduce((sum, apt) => sum + (apt.price || 0), 0);
      const averageTicket = totalVisits > 0 ? Math.round(totalSpent / totalVisits) : 0;

      // Find most recent visit
      const lastVisit = completedAppointments.length > 0
        ? new Date(completedAppointments[0].date) // Already sorted by getAppointmentsByClient
        : undefined;

      // Calculate rebooking rate (simplified - clients who have more than 1 visit)
      const rebookingRate = totalVisits > 1 ? Math.min(95, 50 + (totalVisits * 5)) : 0;

      // Count no-shows and cancellations
      const noShowCount = clientAppointments.filter(apt => apt.status === 'no-show').length;
      const cancellationCount = clientAppointments.filter(apt => apt.status === 'cancelled').length;

      // Update client record
      const updates: Partial<Client> = {
        totalVisits,
        totalSpent,
        averageTicket,
        lastVisit,
        rebookingRate,
        noShowCount,
        cancellationCount
      };

      await this.updateClient(clientId, updates);

      // Recalculate and persist CPI after stats update
      await this.updateCpiScore(clientId);
    } catch (error) {
      console.error('Error updating client stats:', error);
    }
  }

  /**
   * Batch update all client stats
   * Useful for recalculating all CPIs after data migration or fixes
   */
  async recalculateAllClientStats(): Promise<void> {
    const allClients = this.getAllClients();

    for (const client of allClients) {
      await this.updateClientStatsFromAppointments(client.id);
    }
  }

  // 📊 CPI PERSISTENCE METHOD

  /**
   * Calculate and persist CPI score + tier for a client.
   * Call this after appointment completion, client stats update, or tag changes.
   */
  async updateCpiScore(clientId: string): Promise<{ cpiScore: number; cpiTier: 'VIP' | 'Loyal' | 'Regular' | 'New' } | undefined> {
    try {
      const client = this.getClientById(clientId);
      if (!client) {
        console.warn(`Client ${clientId} not found for CPI update`);
        return undefined;
      }

      // Dynamic import to avoid circular dependency
      const { ClientIntelligenceEngine } = await import('./clientIntelligenceEngine');

      const cpiScore = ClientIntelligenceEngine.calculateCPIScore({
        totalSpent: client.totalSpent || 0,
        visitCount: client.totalVisits || 0,
        lastVisit: client.lastVisit,
        referralCount: 0,
        tags: client.tags || []
      });

      const cpiTier = ClientIntelligenceEngine.determineClientTier(
        cpiScore, client.totalSpent, client.totalVisits
      );

      await this.updateClient(clientId, {
        cpiScore,
        cpiTier,
        lastCpiUpdate: new Date().toISOString()
      });

      return { cpiScore, cpiTier };
    } catch (error) {
      console.error('Error updating CPI score:', error);
      return undefined;
    }
  }

  /**
   * Batch recalculate and persist CPI scores for all clients
   */
  async recalculateAllCpiScores(): Promise<void> {
    const allClients = this.getAllClients();
    for (const client of allClients) {
      await this.updateCpiScore(client.id);
    }
  }

  // 🎯 EMERGENCY SERVICE OVERRIDE METHODS

  /**
   * Add a service override for a client
   * Allows trained clients to squeeze-in for normally restricted services
   */
  async addEmergencyServiceOverride(
    clientId: string,
    override: {
      serviceVariationId: string;
      serviceName: string;
      allowEmergencySqueeze: boolean;
      grantedBy: string;
      reason?: string;
    }
  ): Promise<void> {
    try {
      const client = this.getClientById(clientId);
      if (!client) {
        throw new Error('Client not found');
      }

      const existingOverrides = client.emergencyServiceOverrides || [];

      // Check if override already exists for this service
      const existingIndex = existingOverrides.findIndex(
        o => o.serviceVariationId === override.serviceVariationId
      );

      const newOverride = {
        ...override,
        grantedAt: new Date()
      };

      let updatedOverrides;
      if (existingIndex !== -1) {
        // Update existing override
        updatedOverrides = [...existingOverrides];
        updatedOverrides[existingIndex] = newOverride;
      } else {
        // Add new override
        updatedOverrides = [...existingOverrides, newOverride];
      }

      await this.updateClient(clientId, {
        emergencyServiceOverrides: updatedOverrides
      });

    } catch (error) {
      console.error('Error adding emergency service override:', error);
      throw error;
    }
  }

  /**
   * Remove a service override for a client
   */
  async removeEmergencyServiceOverride(
    clientId: string,
    serviceVariationId: string
  ): Promise<void> {
    try {
      const client = this.getClientById(clientId);
      if (!client) {
        throw new Error('Client not found');
      }

      const existingOverrides = client.emergencyServiceOverrides || [];
      const updatedOverrides = existingOverrides.filter(
        o => o.serviceVariationId !== serviceVariationId
      );

      await this.updateClient(clientId, {
        emergencyServiceOverrides: updatedOverrides
      });
    } catch (error) {
      console.error('Error removing emergency service override:', error);
      throw error;
    }
  }

  /**
   * Check if client has override for a specific service
   */
  hasEmergencyServiceOverride(
    clientId: string,
    serviceVariationId: string
  ): boolean {
    const client = this.getClientById(clientId);
    if (!client || !client.emergencyServiceOverrides) {
      return false;
    }

    const override = client.emergencyServiceOverrides.find(
      o => o.serviceVariationId === serviceVariationId
    );

    return override?.allowEmergencySqueeze === true;
  }

  /**
   * Get all service overrides for a client
   */
  getEmergencyServiceOverrides(clientId: string): Client['emergencyServiceOverrides'] {
    const client = this.getClientById(clientId);
    return client?.emergencyServiceOverrides || [];
  }

  // 💳 SAVED PAYMENT METHODS

  /**
   * Add a saved payment method to a client
   */
  async addSavedPaymentMethod(
    clientId: string,
    paymentMethod: SavedPaymentMethod,
    stripeCustomerId: string
  ): Promise<void> {
    try {
      const client = this.getClientById(clientId);
      if (!client) {
        throw new Error('Client not found');
      }

      const existingMethods = client.savedPaymentMethods || [];

      // If this is the first card or marked as default, unset other defaults
      let updatedMethods = existingMethods;
      if (paymentMethod.isDefault) {
        updatedMethods = existingMethods.map(m => ({ ...m, isDefault: false }));
      }

      // Add the new payment method
      updatedMethods = [...updatedMethods, paymentMethod];

      await this.updateClient(clientId, {
        stripeCustomerId,
        savedPaymentMethods: updatedMethods
      });

    } catch (error) {
      console.error('Error adding saved payment method:', error);
      throw error;
    }
  }

  /**
   * Remove a saved payment method from a client
   */
  async removeSavedPaymentMethod(clientId: string, paymentMethodId: string): Promise<void> {
    try {
      const client = this.getClientById(clientId);
      if (!client) {
        throw new Error('Client not found');
      }

      const existingMethods = client.savedPaymentMethods || [];
      const updatedMethods = existingMethods.filter(m => m.id !== paymentMethodId);

      // If we removed the default, set the first remaining card as default
      if (updatedMethods.length > 0 && !updatedMethods.some(m => m.isDefault)) {
        updatedMethods[0].isDefault = true;
      }

      await this.updateClient(clientId, {
        savedPaymentMethods: updatedMethods
      });

    } catch (error) {
      console.error('Error removing saved payment method:', error);
      throw error;
    }
  }

  /**
   * Set a payment method as default
   */
  async setDefaultPaymentMethod(clientId: string, paymentMethodId: string): Promise<void> {
    try {
      const client = this.getClientById(clientId);
      if (!client) {
        throw new Error('Client not found');
      }

      const existingMethods = client.savedPaymentMethods || [];
      const updatedMethods = existingMethods.map(m => ({
        ...m,
        isDefault: m.id === paymentMethodId
      }));

      await this.updateClient(clientId, {
        savedPaymentMethods: updatedMethods
      });

    } catch (error) {
      console.error('Error setting default payment method:', error);
      throw error;
    }
  }

  /**
   * Get all saved payment methods for a client
   */
  getSavedPaymentMethods(clientId: string): SavedPaymentMethod[] {
    const client = this.getClientById(clientId);
    return client?.savedPaymentMethods || [];
  }

  /**
   * Get client's Stripe Customer ID
   */
  getStripeCustomerId(clientId: string): string | undefined {
    const client = this.getClientById(clientId);
    return client?.stripeCustomerId;
  }
}

// Export singleton instance
export const clientDataService = new ClientDataService();

// Initialize from localStorage on import
clientDataService.loadFromLocalStorage();